import React, { useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Box, Paper, CircularProgress, Alert } from '@mui/material';
import { fetchCustomerProfile, updateCustomerProfile, createCustomerProfile } from '../utils/api';
import { useCustomer } from '../contexts/customer-context';
import Header from './Header';

const CustomerProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updatedProfile, setUpdatedProfile] = useState({});
  const [profileExists, setProfileExists] = useState(true);
  const { customerId, customerEmail } = useCustomer();

  useEffect(() => {
    const loadProfile = async () => {
      console.log('Loading profile for customerId:', customerId);
      
      if (!customerId) {
        console.log('Customer ID not found. Redirecting to login.');
        setError('Customer ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const profileData = await fetchCustomerProfile(customerId);

        if (profileData) {
          console.log('Profile data loaded successfully:', profileData);
          // Format the date to YYYY-MM-DD
          const formattedProfile = {
            ...profileData,
            dateOfBirth: profileData.dateOfBirth ? new Date(profileData.dateOfBirth).toISOString().split('T')[0] : ''
          };
          setProfile(formattedProfile);
          setUpdatedProfile(formattedProfile);
          setProfileExists(true);
        } else {
          console.log('No existing profile found. Preparing to create a new profile.');
          setProfileExists(false);
          setUpdatedProfile({ name: '', email: customerEmail, phone: '', dateOfBirth: '' });
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        if (err.response && err.response.status === 404) {
          console.log('Profile not found (404). Preparing to create a new profile.');
          setProfileExists(false);
          setUpdatedProfile({ name: '', email: customerEmail, phone: '', dateOfBirth: '' });
        } else {
          setError('Failed to load profile. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [customerId, customerEmail]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let updatedData;
      const profileData = {
        ...updatedProfile,
        email: customerEmail,
        dateOfBirth: updatedProfile.dateOfBirth ? new Date(updatedProfile.dateOfBirth).toISOString() : null,
        isProfileCompleted: true // Set this to true when submitting
      };
      
      if (profileExists) {
        updatedData = await updateCustomerProfile(customerId, profileData);
      } else {
        updatedData = await createCustomerProfile(customerId, profileData);
        setProfileExists(true);
      }
      // Format the received date back to YYYY-MM-DD
      const formattedUpdatedData = {
        ...updatedData,
        dateOfBirth: updatedData.dateOfBirth ? new Date(updatedData.dateOfBirth).toISOString().split('T')[0] : ''
      };
      setProfile(formattedUpdatedData);
      setUpdatedProfile(formattedUpdatedData);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="sm" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Container maxWidth="sm" sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {profileExists ? 'Customer Profile' : 'Create Your Profile'}
          </Typography>
          {!profileExists && (
            <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              Your Profile incomplete. Please complete your profile information.
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={isEditing || !profileExists ? updatedProfile.name : profile?.name}
              onChange={handleInputChange}
              disabled={!isEditing && profileExists}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={customerEmail || updatedProfile.email}
              disabled={true}
              margin="normal"
              type="email"
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={isEditing || !profileExists ? updatedProfile.phone : profile?.phone}
              onChange={handleInputChange}
              disabled={!isEditing && profileExists}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={(isEditing || !profileExists ? updatedProfile.dateOfBirth : profile?.dateOfBirth) || ''}
              onChange={handleInputChange}
              disabled={!isEditing && profileExists}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              required
              inputProps={{
                max: new Date().toISOString().split('T')[0] // Set max date to today
              }}
            />
            <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'flex-end' }}>
              {profileExists && !isEditing ? (
                <Button onClick={() => setIsEditing(true)} variant="contained" color="primary" fullWidth sx={{ mb: { xs: 1, sm: 0 } }}>
                  Edit Profile
                </Button>
              ) : (
                <>
                  {profileExists && (
                    <Button onClick={() => setIsEditing(false)} sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }} fullWidth>
                      Cancel
                    </Button>
                  )}
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    {profileExists ? 'Save Changes' : 'Create Profile'}
                  </Button>
                </>
              )}
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default CustomerProfile;